/**
 * Provenance & confidence model.
 *
 * Rule of the project (spec §8, §51): no invented number is ever presented as a
 * measured fact. Every quantitative parameter carries a confidence tag, and the
 * UI surfaces the aggregate confidence of a profile.
 */

export type Confidence =
  | 'MEASURED'              // from a real measurement of a real unit
  | 'MANUFACTURER_SPEC'     // published by the manufacturer
  | 'DERIVED'               // computed from measured/spec data by a documented rule
  | 'ESTIMATED'             // inferred from class/topology norms
  | 'MODELLED'              // produced by our physical/behavioural model
  | 'UNKNOWN';

export const CONFIDENCE_ORDER: Confidence[] = [
  'MEASURED', 'MANUFACTURER_SPEC', 'DERIVED', 'ESTIMATED', 'MODELLED', 'UNKNOWN',
];

/** 1.0 = measured, 0.0 = unknown. Used for the aggregate confidence bar. */
export const CONFIDENCE_WEIGHT: Record<Confidence, number> = {
  MEASURED: 1.0,
  MANUFACTURER_SPEC: 0.8,
  DERIVED: 0.6,
  ESTIMATED: 0.4,
  MODELLED: 0.35,
  UNKNOWN: 0.0,
};

export const CONFIDENCE_LABEL: Record<Confidence, string> = {
  MEASURED: 'Measured',
  MANUFACTURER_SPEC: 'Mfr. spec',
  DERIVED: 'Derived',
  ESTIMATED: 'Estimated',
  MODELLED: 'Modelled',
  UNKNOWN: 'Unknown',
};

export const CONFIDENCE_COLOR: Record<Confidence, string> = {
  MEASURED: '#3ddc97',
  MANUFACTURER_SPEC: '#5bc0eb',
  DERIVED: '#c3a6ff',
  ESTIMATED: '#ffc857',
  MODELLED: '#ff9f6e',
  UNKNOWN: '#7a8290',
};

/** A value plus where it came from. */
export interface Prov<T> {
  v: T;
  c: Confidence;
  /** Free-form citation: document, measurement session id, derivation note. */
  src?: string;
  /** Optional +/- tolerance in the value's own unit. */
  tol?: number;
}

export const prov = <T>(v: T, c: Confidence, src?: string, tol?: number): Prov<T> =>
  (src === undefined && tol === undefined ? { v, c } : { v, c, src, tol });

/** Shorthand constructors used heavily by the profile database. */
export const meas = <T>(v: T, src?: string) => prov(v, 'MEASURED', src);
export const spec = <T>(v: T, src?: string) => prov(v, 'MANUFACTURER_SPEC', src);
export const derived = <T>(v: T, src?: string) => prov(v, 'DERIVED', src);
export const est = <T>(v: T, src?: string) => prov(v, 'ESTIMATED', src);
export const modelled = <T>(v: T, src?: string) => prov(v, 'MODELLED', src);

export function unwrap<T>(x: Prov<T> | T, fallback?: T): T {
  if (x && typeof x === 'object' && 'v' in (x as object) && 'c' in (x as object)) {
    return (x as Prov<T>).v;
  }
  return (x as T) ?? (fallback as T);
}

export function confidenceOf(x: unknown): Confidence {
  if (x && typeof x === 'object' && 'c' in (x as object)) return (x as Prov<unknown>).c;
  return 'UNKNOWN';
}

const IS_CONFIDENCE = new Set<string>(CONFIDENCE_ORDER);

/**
 * Walk an object graph and collect every confidence tag found.
 *
 * Two tagging styles coexist and both must be counted:
 *  - `Prov<T>` wrappers `{ v, c }`, used where a single scalar needs its own
 *    provenance;
 *  - a plain `confidence` field on a sub-object (curves, harmonic spectra,
 *    noise profiles, conversion models), which is how the bulk of the database
 *    is authored because it keeps 300 profiles readable.
 *
 * Counting only the first style made the aggregate score read 0 for every
 * built-in profile, which would have shown "Unknown" confidence on data that
 * is in fact well provenanced.
 */
export function collectConfidence(node: unknown, out: Confidence[] = [], depth = 0): Confidence[] {
  if (depth > 12 || node == null) return out;
  if (typeof node !== 'object') return out;
  if (Array.isArray(node)) {
    for (const item of node) collectConfidence(item, out, depth + 1);
    return out;
  }
  const obj = node as Record<string, unknown>;
  if ('v' in obj && 'c' in obj && typeof obj.c === 'string' && IS_CONFIDENCE.has(obj.c)) {
    out.push(obj.c as Confidence);
    return out;
  }
  if (typeof obj.confidence === 'string' && IS_CONFIDENCE.has(obj.confidence)) {
    out.push(obj.confidence as Confidence);
  }
  for (const k of Object.keys(obj)) {
    // Skip bulk sample data and the key we just consumed.
    if (k === 'confidence' || k === 'raw' || k === 'channels' || k === 'samples' || k === 'points') continue;
    collectConfidence(obj[k], out, depth + 1);
  }
  return out;
}

export interface ConfidenceSummary {
  score: number;                       // 0..1
  dominant: Confidence;
  counts: Partial<Record<Confidence, number>>;
  total: number;
}

export function summarizeConfidence(node: unknown): ConfidenceSummary {
  const tags = collectConfidence(node);
  const counts: Partial<Record<Confidence, number>> = {};
  let sum = 0;
  for (const t of tags) {
    counts[t] = (counts[t] ?? 0) + 1;
    sum += CONFIDENCE_WEIGHT[t] ?? 0;
  }
  let dominant: Confidence = 'UNKNOWN';
  let best = -1;
  for (const t of CONFIDENCE_ORDER) {
    const n = counts[t] ?? 0;
    if (n > best) { best = n; dominant = t; }
  }
  return {
    score: tags.length ? sum / tags.length : 0,
    dominant,
    counts,
    total: tags.length,
  };
}
